import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import React, {forwardRef, ReactNode, Ref} from 'react';
import {Link} from './link';

// Global style to override sidebar style for white buttons
if (typeof document !== 'undefined' && !document.getElementById('button-white-override-style')) {
  const style = document.createElement('style');
  style.id = 'button-white-override-style';
  style.textContent = `
    button[data-button-white="true"]:hover svg,
    button[data-button-white="true"]:hover [data-slot="icon"] svg,
    [role="button"][data-button-white="true"]:hover svg,
    a[data-button-white="true"]:hover svg {
      stroke: rgb(59 130 246) !important;
    }
  `;
  document.head.appendChild(style);
}

const styles: any = {
  base: [
    'relative isolate inline-flex items-center justify-center gap-x-2 rounded-lg border text-base/6',
    // Focus
    'focus:outline-none data-[focus]:outline data-[focus]:outline-2 data-[focus]:outline-offset-2 data-[focus]:outline-blue-500',
    // Disabled
    'data-[disabled]:opacity-50',
    // Icon
    '[&>[data-slot=icon]]:-mx-0.5 [&>[data-slot=icon]]:my-0.5 [&>[data-slot=icon]]:size-5 [&>[data-slot=icon]]:shrink-0 [&>[data-slot=icon]]:text-[--btn-icon] [&>[data-slot=icon]]:sm:my-1 [&>[data-slot=icon]]:sm:size-4 forced-colors:[--btn-icon:ButtonText] forced-colors:data-[hover]:[--btn-icon:ButtonText]',
  ],
  sizes: {
    medium: 'px-[calc(theme(spacing[3.5])-1px)] py-[calc(theme(spacing[2.5])-1px)] sm:px-[calc(theme(spacing.3)-1px)] sm:py-[calc(theme(spacing[1.5])-1px)] sm:text-sm/6 font-medium',
    small: 'px-[calc(theme(spacing.2)-1px)] py-[calc(theme(spacing.1)-1px)] sm:px-[calc(theme(spacing[1.5])-1px)] sm:py-[calc(theme(spacing[0.5])-1px)] sm:text-xs/5 font-medium',
  },
  solid: [
    // Optical border, implemented as the button background to avoid corner artifacts
    'border-transparent bg-[--btn-border]',
    // Dark mode: border is rendered on `after` so background is set to button background
    'dark:bg-[--btn-bg]',
    // Button background, implemented as foreground layer to stack on top of pseudo-border layer
    'before:absolute before:inset-0 before:-z-10 before:rounded-[calc(theme(borderRadius.lg)-1px)] before:bg-[--btn-bg]',
    // Background color is moved to control in dark mode so hide `before` pseudo
    'dark:before:hidden',
    // Dark mode: Subtle white outline is applied using a border
    'dark:border-white/5',
    // Shim/overlay, inset to match button foreground and used for hover state
    'after:absolute after:inset-0 after:-z-10 after:rounded-[calc(theme(borderRadius.lg)-1px)]',
    // White overlay on hover
    'after:data-[active]:bg-[--btn-hover-overlay] after:data-[hover]:bg-[--btn-hover-overlay]',
    // Dark mode: `after` layer expands to cover entire button
    'dark:after:-inset-px dark:after:rounded-lg',
  ],
  outline: [
    // Base
    'border-blue-500 text-blue-500 data-[active]:bg-blue-950/[2.5%] data-[hover]:bg-blue-950/[2.5%]',
    // Dark mode
    'dark:border-blue-300 dark:text-blue-300 dark:[--btn-bg:transparent] dark:data-[active]:bg-white/5 dark:data-[hover]:bg-white/5',
    // Icon
    '[--btn-icon:theme(colors.blue.500)] dark:[--btn-icon:theme(colors.blue.300)] data-[active]:[--btn-icon:theme(colors.blue.700)] data-[hover]:[--btn-icon:theme(colors.blue.700)] dark:data-[active]:[--btn-icon:theme(colors.blue.300)] dark:data-[hover]:[--btn-icon:theme(colors.blue.300)]',
  ],
  plain: [
    // Base
    'border-transparent text-zinc-900 data-[active]:bg-green-600 data-[hover]:bg-green-600',
    // Dark mode
    'dark:text-white dark:data-[active]:bg-green-600 dark:data-[hover]:bg-green-600',
    // Icon
    '[--btn-icon:theme(colors.zinc.900)] data-[active]:[--btn-icon:theme(colors.white)] data-[hover]:[--btn-icon:theme(colors.white)] dark:[--btn-icon:theme(colors.white)] dark:data-[active]:[--btn-icon:theme(colors.white)] dark:data-[hover]:[--btn-icon:theme(colors.white)]',
    // Hover text color
    'data-[active]:text-white data-[hover]:text-white dark:data-[active]:text-white dark:data-[hover]:text-white',
  ],
  colors: {
    'dark/zinc': [
      'text-white [--btn-bg:theme(colors.zinc.900)] [--btn-border:theme(colors.zinc.950/90%)] [--btn-hover-overlay:theme(colors.white/10%)]',
      'dark:text-white dark:[--btn-bg:theme(colors.zinc.600)] dark:[--btn-hover-overlay:theme(colors.white/5%)]',
      '[--btn-icon:theme(colors.zinc.400)] data-[active]:[--btn-icon:theme(colors.zinc.300)] data-[hover]:[--btn-icon:theme(colors.zinc.300)]',
    ],
    light: [
      'text-zinc-950 [--btn-bg:white] [--btn-border:theme(colors.zinc.950/10%)] [--btn-hover-overlay:theme(colors.zinc.950/2.5%)] data-[active]:[--btn-border:theme(colors.zinc.950/15%)] data-[hover]:[--btn-border:theme(colors.zinc.950/15%)]',
      'dark:text-white dark:[--btn-hover-overlay:theme(colors.white/5%)] dark:[--btn-bg:theme(colors.zinc.800)]',
      '[--btn-icon:theme(colors.zinc.500)] data-[active]:[--btn-icon:theme(colors.zinc.700)] data-[hover]:[--btn-icon:theme(colors.zinc.700)] dark:[--btn-icon:theme(colors.zinc.500)] dark:data-[active]:[--btn-icon:theme(colors.zinc.400)] dark:data-[hover]:[--btn-icon:theme(colors.zinc.400)]',
    ],
    'dark/white': [
      'text-white [--btn-bg:theme(colors.zinc.900)] [--btn-border:theme(colors.zinc.950/90%)] [--btn-hover-overlay:theme(colors.white/10%)]',
      'dark:text-zinc-950 dark:[--btn-bg:white] dark:[--btn-hover-overlay:theme(colors.zinc.950/5%)]',
      '[--btn-icon:theme(colors.zinc.400)] data-[active]:[--btn-icon:theme(colors.zinc.300)] data-[hover]:[--btn-icon:theme(colors.zinc.300)] dark:[--btn-icon:theme(colors.zinc.500)] dark:data-[active]:[--btn-icon:theme(colors.zinc.400)] dark:data-[hover]:[--btn-icon:theme(colors.zinc.400)]',
    ],
    dark: [
      'text-white [--btn-bg:theme(colors.zinc.900)] [--btn-border:theme(colors.zinc.950/90%)] [--btn-hover-overlay:theme(colors.white/10%)]',
      'dark:[--btn-hover-overlay:theme(colors.white/5%)] dark:[--btn-bg:theme(colors.zinc.800)]',
      '[--btn-icon:theme(colors.zinc.400)] data-[active]:[--btn-icon:theme(colors.zinc.300)] data-[hover]:[--btn-icon:theme(colors.zinc.300)]',
    ],
    white: [
      'text-blue-500 [--btn-bg:white] [--btn-border:transparent] [--btn-hover-overlay:theme(colors.blue.100)]',
      'data-[hover]:text-blue-500 data-[active]:text-blue-500',
      'dark:text-blue-500 dark:[--btn-bg:white] dark:[--btn-hover-overlay:theme(colors.blue.100)]',
      'dark:data-[hover]:text-blue-500 dark:data-[active]:text-blue-500',
      '[--btn-icon:theme(colors.blue.500)] data-[active]:[--btn-icon:theme(colors.blue.500)] data-[hover]:[--btn-icon:theme(colors.blue.500)]',
      'dark:[--btn-icon:theme(colors.blue.500)] dark:data-[active]:[--btn-icon:theme(colors.blue.500)] dark:data-[hover]:[--btn-icon:theme(colors.blue.500)]',
      'border-transparent',
    ],
    zinc: [
      // Light mode: zinc.100 background, light gray border, dark text/icon
      'text-zinc-900 [--btn-bg:theme(colors.zinc.50)] [--btn-border:theme(colors.zinc.200)] [--btn-hover-overlay:theme(colors.green.600)]',
      // Light mode hover: green background, white text/icon
      'data-[active]:text-white data-[hover]:text-white data-[active]:[--btn-bg:theme(colors.green.600)] data-[hover]:[--btn-bg:theme(colors.green.600)] data-[active]:[--btn-border:theme(colors.green.600)] data-[hover]:[--btn-border:theme(colors.green.600)]',
      // Light mode icon colors
      '[--btn-icon:theme(colors.zinc.700)] data-[active]:[--btn-icon:theme(colors.white)] data-[hover]:[--btn-icon:theme(colors.white)]',
      // Dark mode: dark blue/black background, light gray border, white text/icon
      'dark:text-white dark:[--btn-bg:theme(colors.zinc.900)] dark:[--btn-border:theme(colors.zinc.600)] dark:[--btn-hover-overlay:theme(colors.blue.500)]',
      // Dark mode hover: blue background, white text/icon
      'dark:data-[active]:text-white dark:data-[hover]:text-white dark:data-[active]:[--btn-bg:theme(colors.blue.500)] dark:data-[hover]:[--btn-bg:theme(colors.blue.500)] dark:data-[active]:[--btn-border:theme(colors.blue.500)] dark:data-[hover]:[--btn-border:theme(colors.blue.500)]',
      // Dark mode icon colors
      'dark:[--btn-icon:theme(colors.white)] dark:data-[active]:[--btn-icon:theme(colors.white)] dark:data-[hover]:[--btn-icon:theme(colors.white)]',
    ],
    indigo: [
      'text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.indigo.500)] [--btn-border:theme(colors.indigo.600/90%)]',
      '[--btn-icon:theme(colors.indigo.300)] data-[active]:[--btn-icon:theme(colors.indigo.200)] data-[hover]:[--btn-icon:theme(colors.indigo.200)]',
    ],
    cyan: [
      'text-cyan-950 [--btn-bg:theme(colors.cyan.300)] [--btn-border:theme(colors.cyan.400/80%)] [--btn-hover-overlay:theme(colors.white/25%)]',
      '[--btn-icon:theme(colors.cyan.500)]',
    ],
    red: [
      'text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.red.600)] [--btn-border:theme(colors.red.700/90%)]',
      '[--btn-icon:theme(colors.red.300)] data-[active]:[--btn-icon:theme(colors.red.200)] data-[hover]:[--btn-icon:theme(colors.red.200)]',
    ],
    orange: [
      'text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.orange.500)] [--btn-border:theme(colors.orange.600/90%)]',
      '[--btn-icon:theme(colors.orange.300)] data-[active]:[--btn-icon:theme(colors.orange.200)] data-[hover]:[--btn-icon:theme(colors.orange.200)]',
    ],
    amber: [
      'text-amber-950 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.amber.400)] [--btn-border:theme(colors.amber.500/80%)]',
      '[--btn-icon:theme(colors.amber.600)]',
    ],
    yellow: [
      'text-yellow-950 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.yellow.300)] [--btn-border:theme(colors.yellow.400/80%)]',
      '[--btn-icon:theme(colors.yellow.600)] data-[active]:[--btn-icon:theme(colors.yellow.700)] data-[hover]:[--btn-icon:theme(colors.yellow.700)]',
    ],
    lime: [
      'text-lime-950 [--btn-hover-overlay:theme(colors.white/25%)] [--btn-bg:theme(colors.lime.300)] [--btn-border:theme(colors.lime.400/80%)]',
      '[--btn-icon:theme(colors.lime.600)] data-[active]:[--btn-icon:theme(colors.lime.700)] data-[hover]:[--btn-icon:theme(colors.lime.700)]',
    ],
    green: [
      'text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.green.600)] [--btn-border:theme(colors.green.700/90%)]',
      '[--btn-icon:theme(colors.white/60%)] data-[active]:[--btn-icon:theme(colors.white/80%)] data-[hover]:[--btn-icon:theme(colors.white/80%)]',
    ],
    emerald: [
      'text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.emerald.600)] [--btn-border:theme(colors.emerald.700/90%)]',
      '[--btn-icon:theme(colors.white/60%)] data-[active]:[--btn-icon:theme(colors.white/80%)] data-[hover]:[--btn-icon:theme(colors.white/80%)]',
    ],
    teal: [
      'text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.teal.600)] [--btn-border:theme(colors.teal.700/90%)]',
      '[--btn-icon:theme(colors.white/60%)] data-[active]:[--btn-icon:theme(colors.white/80%)] data-[hover]:[--btn-icon:theme(colors.white/80%)]',
    ],
    sky: [
      'text-white [--btn-bg:rgba(255,255,255,0.1)] [--btn-border:rgba(255,255,255,0.1)] [--btn-hover-overlay:rgba(255,255,255,0.15)]',
      'dark:text-white dark:[--btn-bg:rgba(255,255,255,0.1)] dark:[--btn-border:rgba(255,255,255,0.1)] dark:[--btn-hover-overlay:rgba(255,255,255,0.15)]',
      '[--btn-icon:theme(colors.white)] data-[active]:[--btn-icon:theme(colors.white)] data-[hover]:[--btn-icon:theme(colors.white)]',
      'border-white/20',
    ],
    blue: [
      'text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.blue.500)] [--btn-border:theme(colors.blue.700/90%)]',
      // Hover: green background
      'data-[active]:[--btn-bg:theme(colors.green.600)] data-[hover]:[--btn-bg:theme(colors.green.600)] data-[active]:[--btn-border:theme(colors.green.600)] data-[hover]:[--btn-border:theme(colors.green.600)]',
      '[--btn-icon:theme(colors.white)] data-[active]:[--btn-icon:theme(colors.white)] data-[hover]:[--btn-icon:theme(colors.white)]',
    ],
    'blue-secondary': [
      'text-blue-500 [--btn-bg:theme(colors.blue.100)] [--btn-border:theme(colors.blue.200)] [--btn-hover-overlay:theme(colors.blue.200)]',
      // Hover: green background (same as blue variant)
      'data-[active]:[--btn-bg:theme(colors.green.600)] data-[hover]:[--btn-bg:theme(colors.green.600)] data-[active]:[--btn-border:theme(colors.green.600)] data-[hover]:[--btn-border:theme(colors.green.600)]',
      'data-[active]:text-white data-[hover]:text-white',
      '[--btn-icon:theme(colors.blue.500)] data-[active]:[--btn-icon:theme(colors.white)] data-[hover]:[--btn-icon:theme(colors.white)]',
      'data-[disabled]:text-blue-500 data-[disabled]:bg-blue-100 before:data-[disabled]:bg-blue-100 after:data-[disabled]:bg-blue-100',
    ],
    violet: [
      'text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.violet.500)] [--btn-border:theme(colors.violet.600/90%)]',
      '[--btn-icon:theme(colors.violet.300)] data-[active]:[--btn-icon:theme(colors.violet.200)] data-[hover]:[--btn-icon:theme(colors.violet.200)]',
    ],
    purple: [
      'text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.purple.500)] [--btn-border:theme(colors.purple.600/90%)]',
      '[--btn-icon:theme(colors.purple.300)] data-[active]:[--btn-icon:theme(colors.purple.200)] data-[hover]:[--btn-icon:theme(colors.purple.200)]',
    ],
    fuchsia: [
      'text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.fuchsia.500)] [--btn-border:theme(colors.fuchsia.600/90%)]',
      '[--btn-icon:theme(colors.fuchsia.300)] data-[active]:[--btn-icon:theme(colors.fuchsia.200)] data-[hover]:[--btn-icon:theme(colors.fuchsia.200)]',
    ],
    pink: [
      'text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.pink.500)] [--btn-border:theme(colors.pink.600/90%)]',
      '[--btn-icon:theme(colors.pink.300)] data-[active]:[--btn-icon:theme(colors.pink.200)] data-[hover]:[--btn-icon:theme(colors.pink.200)]',
    ],
    rose: [
      'text-white [--btn-hover-overlay:theme(colors.white/10%)] [--btn-bg:theme(colors.rose.500)] [--btn-border:theme(colors.rose.600/90%)]',
      '[--btn-icon:theme(colors.rose.300)] data-[active]:[--btn-icon:theme(colors.rose.200)] data-[hover]:[--btn-icon:theme(colors.rose.200)]',
    ],
    'green-outlined': [
      'text-green-600 !border-green-400',
      'bg-transparent',
      'data-[hover]:bg-green-50 data-[active]:bg-green-100',
      '[--btn-icon:theme(colors.green.600)] data-[hover]:[--btn-icon:theme(colors.green.700)] data-[active]:[--btn-icon:theme(colors.green.800)]',
      'dark:text-green-400 !dark:border-green-400 dark:data-[hover]:bg-green-900/20 dark:data-[active]:bg-green-900/40',
    ],
    'red-outlined': [
      'text-red-600 !border-red-400',
      'bg-transparent',
      'data-[hover]:bg-red-50 data-[active]:bg-red-100',
      '[--btn-icon:theme(colors.red.600)] data-[hover]:[--btn-icon:theme(colors.red.700)] data-[active]:[--btn-icon:theme(colors.red.800)]',
      'dark:text-red-400 !dark:border-red-400 dark:data-[hover]:bg-red-900/20 dark:data-[active]:bg-red-900/40',
    ],
  },
};

type CommonProps = {
  color?: string;
  outline?: boolean;
  plain?: boolean;
  className?: string;
  children?: ReactNode;
  disabled?: boolean;
  size?: 'small' | 'medium';
};

// Props when href is present → it's a link
type AnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string;
};

// Props when no href → it's a button
type CatalystButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  href?: undefined;
} & Headless.ButtonProps;

export type ButtonProps = CommonProps & (AnchorProps | CatalystButtonProps);

export const Button = forwardRef<Ref<HTMLElement>, ButtonProps>(function Button(
  {color, outline, plain, className, children, size = 'medium', ...props},
  ref,
) {
  const isWhiteButton = color === 'white' && !outline && !plain;

  let classes = clsx(
    className,
    styles.base,
    styles.sizes[size],
    props.disabled ? ' cursor-not-allowed' : 'cursor-pointer',
    outline
      ? styles.outline
      : plain
      ? styles.plain
      : clsx(styles.solid, styles.colors[color ?? 'dark/zinc']),
  );

  if ('href' in props) {
    const anchorProps = props as AnchorProps;

    return (
      <Link
        {...anchorProps}
        className={classes}
        data-button-white={isWhiteButton ? 'true' : undefined}
        ref={ref as Ref<HTMLAnchorElement>}>
        <TouchTarget>{children}</TouchTarget>
      </Link>
    );
  }

  return (
    <Headless.Button
      {...props}
      className={clsx(classes, 'cursor-default')}
      data-button-white={isWhiteButton ? 'true' : undefined}
      ref={ref as Ref<HTMLButtonElement>}>
      <TouchTarget>{children}</TouchTarget>
    </Headless.Button>
  );
});

/**
 * Expand the hit area to at least 44×44px on touch devices
 */
export function TouchTarget({children}: {children: any}) {
  return (
    <>
      <span
        className="absolute left-1/2 top-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 [@media(pointer:fine)]:hidden"
        aria-hidden="true"
      />
      {children}
    </>
  );
}
